"use client"

import type { ReactNode } from "react"

import { Link, usePathname } from "@/i18n/navigation"
import { categoryPageHref } from "@/lib/menu-presentation"

/**
 * Link to a page of the menu — a category's own page (`categoryId`) or the
 * category overview (`/menu`, no `categoryId`) — that marks itself current
 * (`aria-current="page"`, `data-active`) while that page is open.
 *
 * Client-side on purpose: rails live in the browse segment's layout, which
 * Next.js reuses across navigations between categories, so a server-rendered
 * "current" would stay on whichever category was opened first.
 */
export function CategoryPageLink({
  categoryId,
  className,
  children,
}: {
  categoryId?: string
  className?: string
  children: ReactNode
}) {
  const pathname = usePathname()
  const href = categoryId === undefined ? "/menu" : categoryPageHref(categoryId)
  const current = pathname === href
  return (
    <Link
      href={href}
      aria-current={current ? "page" : undefined}
      data-active={current ? "" : undefined}
      className={className}
    >
      {children}
    </Link>
  )
}
